import { SvgAnimationProps } from '../types/svgTypes';

/**
 * 从 from/to 属性中提取旋转中心点
 * 例如: "0 50 50" -> { angle: 0, cx: 50, cy: 50 }
 * 例如: "360" -> { angle: 360, cx: undefined, cy: undefined }
 */
const parseRotateValue = (value: string | null): { angle: number; cx?: number; cy?: number } => {
  if (!value) return { angle: 0 };
  
  const parts = value.trim().split(/\s+/);
  const angle = parseFloat(parts[0]) || 0;
  
  if (parts.length >= 3) {
    return {
      angle,
      cx: parseFloat(parts[1]),
      cy: parseFloat(parts[2]),
    };
  }
  
  return { angle };
};

/**
 * 从 values 属性中提取中心点（针对 rotate 类型）
 * 例如: "0 50 50;180 50 50;360 50 50" -> "50 50"
 */
const extractCenterFromValues = (values: string | null): string | undefined => {
  if (!values) return undefined;
  
  const firstValue = values.split(';')[0];
  const parsed = parseRotateValue(firstValue);
  
  if (parsed.cx !== undefined && parsed.cy !== undefined) {
    return `${parsed.cx} ${parsed.cy}`;
  }
  
  return undefined;
};

/**
 * 尝试计算 SVG 内容的边界框中心点
 */
const calculateSvgCenter = (svgContent: string): string | undefined => {
  try {
    // 创建临时容器来计算 BBox
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`;
    document.body.appendChild(tempDiv);
    
    const svgEl = tempDiv.querySelector('svg');
    const firstChild = svgEl?.firstElementChild as SVGGraphicsElement | null;
    
    if (firstChild && typeof firstChild.getBBox === 'function') {
      const bbox = firstChild.getBBox();
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;
      document.body.removeChild(tempDiv);
      return `${cx} ${cy}`;
    }
    
    document.body.removeChild(tempDiv);
  } catch (e) {
    console.warn('计算 SVG 中心点失败:', e);
  }
  
  return undefined;
};

/**
 * 从 SVG 内容中提取动画属性
 * 用于在属性面板中显示 SVG 原有的动画配置
 * 
 * 注意：提取的动画会标记 isOriginalAnimation = true
 * 这表示 SVG 中已经有动画标签，不需要重新注入动画
 */
export const extractAnimationFromSvg = (svgContent: string): SvgAnimationProps | null => {
  if (!svgContent) return null;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`, 'image/svg+xml');

    // 查找 animateTransform 元素
    const animateTransform = doc.querySelector('animateTransform');
    if (animateTransform) {
      const type = animateTransform.getAttribute('type');
      const dur = animateTransform.getAttribute('dur');
      const repeatCount = animateTransform.getAttribute('repeatCount');
      const from = animateTransform.getAttribute('from');
      const to = animateTransform.getAttribute('to');
      const values = animateTransform.getAttribute('values');

      let animationType: SvgAnimationProps['animationType'] = 'none';
      let transformOrigin: string | undefined;
      let translateValues: string | undefined;
      let scaleValues: string | undefined;

      if (type === 'rotate') {
        animationType = 'rotate';
        
        // 从 from/to 提取中心点
        const fromParsed = parseRotateValue(from);
        if (fromParsed.cx !== undefined && fromParsed.cy !== undefined) {
          transformOrigin = `${fromParsed.cx} ${fromParsed.cy}`;
        } else {
          // 尝试从 values 属性提取
          transformOrigin = extractCenterFromValues(values);
        }
        
        // 如果还是没有找到中心点，尝试计算
        if (!transformOrigin) {
          transformOrigin = calculateSvgCenter(svgContent);
        }
        
      } else if (type === 'scale') {
        animationType = 'scale';
        scaleValues = values || undefined;
        // scale 也可能有中心点
        transformOrigin = extractCenterFromValues(values) || calculateSvgCenter(svgContent);
        
      } else if (type === 'translate') {
        animationType = 'translate';
        translateValues = values || undefined;
      }

      return {
        animationType,
        duration: dur || '2s',
        repeatCount: repeatCount || 'indefinite',
        easing: 'linear',
        paused: false,
        isOriginalAnimation: true,
        transformOrigin,
        translateValues,
        scaleValues,
      };
    }

    // 查找 animate 元素（用于 opacity 等）
    const animate = doc.querySelector('animate');
    if (animate) {
      const attributeName = animate.getAttribute('attributeName');
      const dur = animate.getAttribute('dur');
      const repeatCount = animate.getAttribute('repeatCount');
      const values = animate.getAttribute('values');

      if (attributeName === 'opacity') {
        return {
          animationType: 'opacity',
          duration: dur || '2s',
          repeatCount: repeatCount || 'indefinite',
          easing: 'linear',
          paused: false,
          isOriginalAnimation: true,
          opacityValues: values || undefined,
        };
      }
    }

    return null;
  } catch (error) {
    console.error('提取 SVG 动画失败:', error);
    return null;
  }
};

/**
 * 递归删除所有动画标签
 */
const removeAllAnimations = (element: Element): void => {
  const toRemove: Element[] = [];
  
  for (const child of Array.from(element.children)) {
    const tagName = child.tagName.toLowerCase();
    if (['animate', 'animatetransform', 'animatemotion', 'set'].includes(tagName)) {
      toRemove.push(child);
    } else {
      removeAllAnimations(child);
    }
  }
  
  toRemove.forEach(el => el.remove());
};

/**
 * 查找最佳的动画注入目标
 */
const findAnimationTarget = (root: Element): Element | null => {
  for (const child of Array.from(root.children)) {
    const tagName = child.tagName.toLowerCase();
    
    if (['defs', 'filter', 'clippath', 'mask', 'symbol', 'style'].includes(tagName)) {
      continue;
    }
    
    if (tagName === 'g') {
      const hasVisibleContent = child.querySelector('path, circle, rect, line, ellipse, polygon, polyline, text, image');
      if (hasVisibleContent) {
        return child;
      }
    }
    
    if (['path', 'circle', 'rect', 'line', 'ellipse', 'polygon', 'polyline', 'text'].includes(tagName)) {
      return root;
    }
  }
  
  for (const child of Array.from(root.children)) {
    const tagName = child.tagName.toLowerCase();
    if (!['defs', 'filter', 'clippath', 'mask', 'symbol', 'style'].includes(tagName)) {
      return child;
    }
  }
  
  return null;
};

/**
 * 将 easing 名称转换为 SVG keySplines 值
 */
const getKeySplines = (easing?: string): string => {
  const easingMap: Record<string, string> = {
    'linear': '0 0 1 1',
    'ease': '0.25 0.1 0.25 1',
    'ease-in': '0.42 0 1 1',
    'ease-out': '0 0 0.58 1',
    'ease-in-out': '0.42 0 0.58 1',
  };
  return easingMap[easing ?? 'ease'] ?? '0.25 0.1 0.25 1';
};

/**
 * 根据动画配置生成 SVG 原生动画标签
 * 添加 pointer-events="none" 确保不干扰点击事件
 * 使用 transformOrigin 保持正确的变换中心点
 */
export const generateSvgAnimationTags = (
  animation: SvgAnimationProps | undefined,
  svgContent?: string
): string => {
  if (!animation || !animation.animationType || animation.animationType === 'none') {
    return '';
  }
  
  const { 
    animationType, 
    duration = '2s', 
    repeatCount = 'indefinite', 
    easing = 'ease',
    transformOrigin,
    translateValues,
    scaleValues,
    opacityValues,
  } = animation;
  
  const keySplines = getKeySplines(easing);
  const calcMode = easing === 'linear' ? 'linear' : 'spline';
  const pointerEventsAttr = 'style="pointer-events: none;"';
  
  // 获取中心点，如果没有设置则尝试计算
  let center = transformOrigin;
  if (!center && svgContent && (animationType === 'rotate' || animationType === 'scale')) {
    center = calculateSvgCenter(svgContent);
  }
  
  switch (animationType) {
    case 'rotate': {
      // 如果有中心点，使用 "angle cx cy" 格式
      const fromValue = center ? `0 ${center}` : '0';
      const toValue = center ? `360 ${center}` : '360';
      
      return `<animateTransform 
        attributeName="transform" 
        type="rotate" 
        from="${fromValue}" 
        to="${toValue}" 
        dur="${duration}" 
        repeatCount="${repeatCount}"
        calcMode="${calcMode}"
        ${calcMode === 'spline' ? `keyTimes="0;1" keySplines="${keySplines}"` : ''}
        ${pointerEventsAttr}
      />`;
    }
      
    case 'scale': {
      // scale 使用 values 格式，可以保持中心点
      const values = scaleValues || '1;1.2;1';
      
      return `<animateTransform 
        attributeName="transform" 
        type="scale" 
        values="${values}" 
        dur="${duration}" 
        repeatCount="${repeatCount}"
        calcMode="${calcMode}"
        ${calcMode === 'spline' ? `keyTimes="0;0.5;1" keySplines="${keySplines};${keySplines}"` : ''}
        ${pointerEventsAttr}
      />`;
    }
      
    case 'translate': {
      const values = translateValues || '0,0;10,0;0,0';
      
      return `<animateTransform 
        attributeName="transform" 
        type="translate" 
        values="${values}" 
        dur="${duration}" 
        repeatCount="${repeatCount}"
        calcMode="${calcMode}"
        ${calcMode === 'spline' ? `keyTimes="0;0.5;1" keySplines="${keySplines};${keySplines}"` : ''}
        ${pointerEventsAttr}
      />`;
    }
      
    case 'opacity': {
      const values = opacityValues || '1;0.3;1';
      
      return `<animate 
        attributeName="opacity" 
        values="${values}" 
        dur="${duration}" 
        repeatCount="${repeatCount}"
        calcMode="${calcMode}"
        ${calcMode === 'spline' ? `keyTimes="0;0.5;1" keySplines="${keySplines};${keySplines}"` : ''}
        ${pointerEventsAttr}
      />`;
    }
      
    default:
      return '';
  }
};

/**
 * 将动画标签注入到 SVG 内容中
 * 1. 先递归删除所有已存在的动画标签
 * 2. 在合适的位置注入新动画
 */
export const injectAnimationToSvg = (
  svgContent: string,
  animation: SvgAnimationProps | undefined
): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`,
    'image/svg+xml'
  );

  const svgRoot = doc.documentElement;
  
  // 1. 递归删除所有已存在的动画标签
  removeAllAnimations(svgRoot);

  // 2. 如果动画类型为 none 或未设置，直接返回（不注入新动画）
  if (!animation || animation.animationType === 'none' || !animation.animationType) {
    return svgRoot.innerHTML;
  }

  // 3. 生成新的动画标签（传入 svgContent 用于计算中心点）
  const animationTags = generateSvgAnimationTags(animation, svgContent);
  if (!animationTags) {
    return svgRoot.innerHTML;
  }

  // 4. 找到最佳注入位置
  const target = findAnimationTarget(svgRoot);
  
  if (target) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg">${animationTags}</svg>`;
    const animSvg = tempDiv.firstElementChild;
    
    if (animSvg) {
      const animationElements = Array.from(animSvg.children);
      animationElements.forEach(animEl => {
        if (target.firstChild) {
          target.insertBefore(doc.importNode(animEl, true), target.firstChild);
        } else {
          target.appendChild(doc.importNode(animEl, true));
        }
      });
    }
  }

  return svgRoot.innerHTML;
};

/**
 * 从 SVG 内容中移除所有动画标签
 */
export const removeAnimationFromSvg = (svgContent: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`,
    'image/svg+xml'
  );

  removeAllAnimations(doc.documentElement);

  return doc.documentElement.innerHTML;
};